import APP_CONFIG from '@/config/app';
import { AnimatePresence, motion } from 'framer-motion';
import {
  CSSProperties,
  FC,
  PropsWithChildren,
  ReactNode,
  RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
const visualizeElementsAndSpacing = false;
const borderClassToVisualizeElementsAndSpacing = visualizeElementsAndSpacing
  ? 'border-white border'
  : '';
const { ALTERNATIVE_BACKDROP_BLUR } = APP_CONFIG.STYLES;
const mainGap = APP_CONFIG.STYLES.MAIN_GAP;
const mainRoundedness = APP_CONFIG.STYLES.MAIN_ROUNDEDNESS;
const spacingConfig = {
  bodyPadding: `p${mainGap}`,
  bodyFullBleedPadding: 'p-0',
};
const colorConfig = {
  bodyBackground: `bg-primary/5 backdrop-blur${ALTERNATIVE_BACKDROP_BLUR}`,
  bodyBorder: 'border border-solid border-primary/5',
};
const roundednessConfig = {
  bodyRoundedness: `rounded${mainRoundedness}`,
};
type Props = {
  trigger?: ReactNode;
  align?: 'left' | 'right';
  alignToElementRef?: RefObject<HTMLElement>;
  triggerRef?: RefObject<HTMLInputElement | HTMLElement>;
  className?: string;
  isFullBleed?: boolean;
  width?: number | string;
} & PropsWithChildren;
const Tooltip: FC<Props> = ({
  children,
  trigger,
  align = 'left',
  alignToElementRef,
  triggerRef,
  className = '',
  isFullBleed = false,
  width = 400,
}) => {
  const defaultTriggerRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState<CSSProperties>({});
  const tooltipRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const calculateTooltipPosition = () => {
      const referenceElement =
        alignToElementRef?.current ||
        triggerRef?.current ||
        defaultTriggerRef.current;
      if (referenceElement && tooltipRef.current) {
        const rect = referenceElement.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const dynamicWidth = tooltipRect.width;
        const referenceWidth = rect.width;
        let baseStyle: CSSProperties = {
          top: rect.bottom + window.scrollY + 8,
          position: 'absolute',
          width: alignToElementRef ? referenceWidth : width || dynamicWidth,
          willChange: 'transform',
          transform: 'translateZ(0)',
          zIndex: 50,
        };
        baseStyle.left =
          align === 'right'
            ? rect.right + window.scrollX - (Number(baseStyle.width) || 0)
            : rect.left + window.scrollX;
        setTooltipStyle(baseStyle);
      }
    };
    if (isHovered) {
      calculateTooltipPosition();
      const resizeObserver = new ResizeObserver(() => {
        calculateTooltipPosition();
      });
      const element =
        alignToElementRef?.current ||
        triggerRef?.current ||
        defaultTriggerRef.current;
      if (element) {
        resizeObserver.observe(element);
      }
      return () => {
        if (element) {
          resizeObserver.unobserve(element);
        }
      };
    }
  }, [isHovered, align, alignToElementRef, triggerRef, width]);
  const renderTooltipContent = () => {
    return createPortal(
      <AnimatePresence>
        {isHovered && (
          <motion.div
            initial="initial"
            animate="open"
            exit="closed"
            variants={APP_CONFIG.ANIMATION_SETTINGS.TOOLTIP}
            transition={APP_CONFIG.ANIMATION_SETTINGS.TOOLTIP.transition}
            className={`absolute scrollbar-hide 
              ${isFullBleed
                ? spacingConfig.bodyFullBleedPadding
                : spacingConfig.bodyPadding
              } 
              ${colorConfig.bodyBackground} 
              ${roundednessConfig.bodyRoundedness} 
              ${colorConfig.bodyBorder} 
              ${className} 
              ${borderClassToVisualizeElementsAndSpacing}`}
            style={tooltipStyle}
            ref={tooltipRef}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>,
      document.body,
    );
  };
  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);
  return (
    <div
      ref={triggerRef || alignToElementRef ? undefined : defaultTriggerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`relative inline-block ${borderClassToVisualizeElementsAndSpacing}`}
      style={{
        lineHeight: 0,
        verticalAlign: 'middle',
      }}
    >
      {trigger && trigger}
      {renderTooltipContent()}
    </div>
  );
};
export default Tooltip;
